import { Customer, INVOICE_ACCOUNT_DENIED, permissionAllowed } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonItem } from "@ionic/react";
import { Locale } from "../../../../setup/locale";
import { useMyPermissions, useMyProfile } from "../../../Login/LoginProvider";
import PriceListTypeAhead from "../../../PriceList/PriceListTypeAhead";
import { useModifyCustomer } from "../../../ReactData/customer";
import SettingsCol from "../../../Settings/SettingsCol/SettingsCol";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsSelect from "../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { InvoiceSettingsProps } from "./types";
import SettingsTitle from "../../../Settings/SettingsTitle/SettingsTitle";
import SettingsLabel from "../../../Settings/SettingsComponents/SettingsLabel/SettingsLabel";

const InvoiceAddressTranslationKeyMap: Record<string, string> =
{
    "fax": "COM_CONTACT_FAX",
    "zip": "COM_FACPMANAGEMENT_FACP_FIELD_POSTAL_CODE_LABEL",
    "city": "COM_FACPMANAGEMENT_CUSTOMER_FIELD_PLACE_LABEL",
    "region": "PLG_FINDER_QUERY_FILTER_BRANCH_S_REGION",
    "country": "PLG_USER_PROFILE_FIELD_COUNTRY_LABEL",
    "ustIdNr": "[REACT_UST-IDNR.]",
    "address1": "[REACT_PHONEBOOK_DEVICE_DETAIL_ADDRESS_1]",
    "address2": "[REACT_PHONEBOOK_DEVICE_DETAIL_ADDRESS_2]",
    "lastName": "COM_FACPMANAGEMENT_USERS_LASTNAME",
    "firstName": "COM_FACPMANAGEMENT_USERS_FIRSTNAME",
    "firstTitle": "COM_CONTENT_HEADING_TITLE",
    "salutation": "COM_FACPMANAGEMENT_USERS_SALUTATION",
    "companyName": "COM_VIRTUEMART_SHOPPER_FORM_COMPANY_NAME",
    "phoneNumber": "COM_FACPMANAGEMENT_LOGS_SMS_PHONE_NUMBER",
    "invoiceEmail": "[REACT_EMAIL_FOR_INVOICE_RECEIPT]",
    "secondFirstName": "[REACT_SECOND_FIRST_NAME]",
    "mobilePhoneNumber": "[REACT_MOBILE_PHONE_NUMBER]",
    "notifyLicenseExpiration": "[REACT_E-MAIL_PURCHASING_OFFICER]",
    "invoiceAccountNumber": "COM_VMINVOICE3_CUSTOMER_NUMBER",
    "company": ""
};

InvoiceAddressTranslationKeyMap.company = InvoiceAddressTranslationKeyMap.companyName;

const InvoiceSettings: React.FC<InvoiceSettingsProps> = ({ customer, }) =>
{

    const myPermissions = useMyPermissions();
    const allowedToEdit = permissionAllowed(myPermissions.createCustomer || 0);

    const { translate: t } = useL10n();
    const _modifyCustomer = useModifyCustomer();

    function modifyCustomer(data: Customer)
    {
        if (customer.id) {
            _modifyCustomer(customer.id, data);
        }
    }

    const me = useMyProfile();

    const userIsFromPayingCustomer = (customer.id && me.customerId === customer.id);

    return <>
        <SettingsCol>

            <SettingsSelect
                text={"[REACT_CONTRACT_DURATION]"}
                value={customer.duration}
                disabled={!myPermissions.licenseManagement}
                onSelect={(duration) =>
                {
                    modifyCustomer({ duration } as Customer);
                }}>
                <SettingsSelectOption text={("1 " + t("COM_CONTENT_MONTH")) as Locale} value={"P1M"} />
                <SettingsSelectOption text={("12 " + t("[REACT_MONTHS]")) as Locale} value={"P12M"} />
            </SettingsSelect>

            <SettingsCheckbox
                disabled={!allowedToEdit}
                checked={customer?.collectiveInvoice || false}
                onChange={(checked) =>
                {
                    modifyCustomer({ collectiveInvoice: checked } as Customer);
                }}
                text={"COM_FACPMANAGEMENT_CUSTOMER_ACCOUNT_PERMISSIONS_COMMON_COLLECTIVE_INVOICE"}
            />

            <SettingsTextInput
                text={"[REACT_E-MAIL_PURCHASING_OFFICER]"}
                value={customer.notifyLicenseExpiration}
                disabled={!userIsFromPayingCustomer}
                onChange={(notifyLicenseExpiration) =>
                {
                    if (customer?.notifyLicenseExpiration === notifyLicenseExpiration) return;
                    modifyCustomer({ notifyLicenseExpiration } as Customer);
                }}
            />
            <SettingsTextInput
                text={"COM_FACPMANAGEMENT_LOGS_BALANCE_ORDERNR"}
                value={customer.orderNumber?.toString()}
                disabled={!allowedToEdit}
                onChange={(orderNumber) =>
                {
                    if (customer?.orderNumber === orderNumber) return;
                    modifyCustomer({ orderNumber } as Customer);
                }}
            />

            <SettingsTextInput
                lines={myPermissions.licenseManagement ? "none" : "full"}
                text={"COM_VMINVOICE3_CUSTOMER_NUMBER"}
                endText={' LST'}
                inputType="number"
                disabled={!myPermissions.licenseManagement}
                value={customer.invoiceAccountNumber?.toString()}
                onChange={(invoiceAccountNumber) =>
                {
                    if ((customer?.invoiceAccountNumber + "") === (invoiceAccountNumber + "") && (invoiceAccountNumber + "").length < 6) return;
                    modifyCustomer({ invoiceAccountNumber } as Customer);
                }}
            />

            {myPermissions.licenseManagement && <>

                <IonItem>
                    <IonButton
                        disabled={customer.invoiceAccountNumber === INVOICE_ACCOUNT_DENIED}
                        color="danger"
                        onClick={() =>
                        {
                            modifyCustomer({ invoiceAccountNumber: INVOICE_ACCOUNT_DENIED } as Customer);
                        }}
                    >{t("[REACT_REVOKE_INVOICE_ACCOUNT_NUMBER]")}</IonButton>
                </IonItem>

                <PriceListTypeAhead
                    priceListId={customer.priceListId || ""}
                    onChange={(priceListId) =>
                    {
                        modifyCustomer({ priceListId } as Customer);
                    }}
                />
            </>
            }

        </SettingsCol>
        <SettingsCol>
            {customer.invoiceAccountRequest && <>
                <SettingsTitle
                    text={t('[REACT_INVOICE_REQUEST_DETAILS]')}
                />
                {Object.entries(customer.invoiceAccountRequest).map(([key, value]) =>
                {

                    return <SettingsLabel

                        text={t(InvoiceAddressTranslationKeyMap[key])}
                        value={value}
                    />;

                })}

            </>
            }
        </SettingsCol>
        <SettingsCol>

        </SettingsCol>

    </>;
};

export default InvoiceSettings;
